import {
  apiGetUserTrips,
  API_BASE_URL,
  type UserTrip,
} from '../api/profile/userProfileApi'

export interface TripReview {
  tripId: number | string
  bookingReference: string
  rating: number // 1 - 5
  feedback: string
  tags: string[]
  route: {
    from: string
    to: string
  }
  vehicle?: string
  driverName?: string
  submittedAt: string
  customerName?: string
  customerEmail?: string
}

const REVIEWED_STORAGE_KEY = 'salzburg_reviewed_trip_ids'
const REVIEWS_STORAGE_KEY = 'salzburg_trip_reviews'
const SESSION_DISMISSED_KEY = 'salzburg_session_dismissed_reviews'

export const TRIP_REVIEW_TRIGGER_EVENT = 'salzburg-open-trip-review'
export const TRIP_REVIEW_SUBMITTED_EVENT = 'salzburg-trip-reviewed'

export function getReviewedTripIds(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(REVIEWED_STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export function isTripReviewed(tripId: number | string): boolean {
  const ids = getReviewedTripIds()
  return ids.includes(String(tripId))
}

export function getTripReview(tripId: number | string): TripReview | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(REVIEWS_STORAGE_KEY)
    if (!raw) return null
    const reviews: TripReview[] = JSON.parse(raw)
    return reviews.find((r) => String(r.tripId) === String(tripId)) || null
  } catch {
    return null
  }
}

export function isReviewDismissedForSession(tripId: number | string): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = sessionStorage.getItem(SESSION_DISMISSED_KEY)
    const dismissed: string[] = raw ? JSON.parse(raw) : []
    return dismissed.includes(String(tripId))
  } catch {
    return false
  }
}

export function dismissReviewForSession(tripId: number | string): void {
  if (typeof window === 'undefined') return
  try {
    const raw = sessionStorage.getItem(SESSION_DISMISSED_KEY)
    const dismissed: string[] = raw ? JSON.parse(raw) : []
    if (!dismissed.includes(String(tripId))) {
      dismissed.push(String(tripId))
      sessionStorage.setItem(SESSION_DISMISSED_KEY, JSON.stringify(dismissed))
    }
  } catch {
    // Ignore session storage errors
  }
}

export function saveTripReview(review: TripReview): void {
  if (typeof window === 'undefined') return

  try {
    // 1. Save to reviews list
    const rawReviews = localStorage.getItem(REVIEWS_STORAGE_KEY)
    const reviews: TripReview[] = rawReviews ? JSON.parse(rawReviews) : []
    reviews.unshift(review)
    localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(reviews))

    // 2. Mark trip as reviewed
    const reviewedIds = getReviewedTripIds()
    if (!reviewedIds.includes(String(review.tripId))) {
      reviewedIds.push(String(review.tripId))
      localStorage.setItem(REVIEWED_STORAGE_KEY, JSON.stringify(reviewedIds))
    }

    // 3. Bridge to admin feedback storage so admin sees the review
    syncToAdminFeedback(review)

    // 4. Notify all components
    window.dispatchEvent(
      new CustomEvent(TRIP_REVIEW_SUBMITTED_EVENT, { detail: review })
    )
  } catch (err) {
    console.error('Failed to save trip review', err)
  }
}

/**
 * Bridges user's review into the Admin Dashboard's feedback store
 */
function syncToAdminFeedback(review: TripReview) {
  try {
    const adminKey = 'salzburg_feedback_entries'
    const raw = localStorage.getItem(adminKey)
    const list = raw ? JSON.parse(raw) : []

    const feedbackEntry = {
      id: `FB-${Date.now()}`,
      customer: {
        name: review.customerName || 'Customer',
        email: review.customerEmail || 'customer@example.com',
      },
      bookingId: review.bookingReference,
      service: 'Transfers',
      route: {
        from: review.route.from,
        to: review.route.to,
      },
      rating: review.rating,
      review: review.feedback
        ? `${review.tags.length > 0 ? `[${review.tags.join(', ')}] ` : ''}${review.feedback}`
        : review.tags.join(', ') || 'Great trip!',
      driver: review.driverName || 'Professional Chauffeur',
      status: 'new',
      submittedAt: review.submittedAt,
    }

    list.unshift(feedbackEntry)
    localStorage.setItem(adminKey, JSON.stringify(list))

    // Increment notification badge if needed
    localStorage.setItem('salzburg_feedback_seen', 'false')
    window.dispatchEvent(new Event('dashboard-badges-updated'))
  } catch {
    // non-fatal
  }
}

/**
 * Submit trip review to backend API with automatic local storage fallback
 */
export async function apiSubmitTripReview(
  review: TripReview,
): Promise<{ success: boolean; message: string }> {
  // Save locally first so the trip is immediately marked reviewed in the client
  saveTripReview(review)

  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null

  const payload = {
    booking_id: review.bookingReference,
    trip_id: review.tripId,
    rating: review.rating,
    comment: review.feedback,
    review: review.feedback
      ? `${review.tags.length > 0 ? `[${review.tags.join(', ')}] ` : ''}${review.feedback}`
      : review.tags.join(', ') || 'Great trip!',
    tags: review.tags,
    driver: review.driverName || 'Professional Chauffeur',
    route_from: review.route.from,
    route_to: review.route.to,
    customer_name: review.customerName || 'Customer',
    customer_email: review.customerEmail || 'customer@example.com',
    submitted_at: review.submittedAt,
  }

  try {
    const endpoints = [
      `${API_BASE_URL}/api/user/trips/${review.tripId}/review/`,
      `${API_BASE_URL}/api/admin/feedback/`,
      `${API_BASE_URL}/api/user/feedback/`,
    ]

    for (const url of endpoints) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        })

        if (response.ok) {
          const resJson = await response.json().catch(() => null)
          return {
            success: true,
            message: resJson?.message || 'Thank you! Your review has been submitted successfully.',
          }
        }
      } catch {
        // Try fallback endpoint
      }
    }
  } catch (err) {
    console.warn('Backend review sync failed, cached locally:', err)
  }

  return {
    success: true,
    message: 'Thank you! Your review has been submitted successfully.',
  }
}

/**
 * Finds the user's most recent completed trip that has not yet been reviewed and was not dismissed in this session.
 * Only triggers if the user is authenticated.
 */
export async function findPendingReviewTrip(): Promise<UserTrip | null> {
  if (typeof window === 'undefined') return null

  // Ensure only authenticated users are evaluated
  const token = localStorage.getItem('auth_token')
  if (!token) return null

  try {
    const res = await apiGetUserTrips()
    const trips: UserTrip[] = res.success && res.data ? res.data : []
    if (!trips.length) return null

    // Find all completed trips that are not yet reviewed and not dismissed in this session
    const completedTrips = trips.filter((trip) => {
      const isCompleted =
        trip.status === 'Completed' ||
        String(trip.status).toLowerCase() === 'completed'

      if (!isCompleted) return false
      if (isTripReviewed(trip.id)) return false
      if (isReviewDismissedForSession(trip.id)) return false

      return true
    })

    if (!completedTrips.length) return null

    // Sort to find the most recent completed trip
    const sorted = [...completedTrips].sort((a, b) => {
      const dateA = new Date(a.date).getTime()
      const dateB = new Date(b.date).getTime()
      if (!isNaN(dateA) && !isNaN(dateB)) {
        return dateB - dateA
      }
      return Number(b.id) - Number(a.id)
    })

    return sorted[0] || null
  } catch {
    return null
  }
}
